import { motion } from 'framer-motion';
import { ChevronDown, Leaf, Users, TrendingUp, Globe, Heart, Mail, Phone, MapPin } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { SECTION_IDS } from '@/lib/index';
import { companyInfo, operationalModel, sdgGoals, sustainabilityCommitments } from '@/data/index';
import { SDGCard, FeatureCard, CommitmentCard } from '@/components/Cards';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { Button } from '@/components/ui/button';

export default function Home() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full">
      <section id={SECTION_IDS.HERO} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_AGRICULTURE_1}
            alt="Planalto Central Angola"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center space-y-8"
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
              SojaTerra – Plataforma Agrícola no Planalto Central
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
              Produção estruturada, visão regional e compromisso com o desenvolvimento sustentável.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                size="lg"
                onClick={() => scrollToSection(SECTION_IDS.SOBRE)}
                className="text-lg"
              >
                Sobre o Projeto
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection(SECTION_IDS.CONTACTO)}
                className="text-lg"
              >
                Contacto Institucional
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, ...springPresets.gentle }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <button
              onClick={() => scrollToSection(SECTION_IDS.SOBRE)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Scroll para próxima seção"
            >
              <ChevronDown className="w-8 h-8 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.SOBRE} className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Sobre a SojaTerra</h2>
              <div className="w-24 h-1 bg-primary mx-auto mb-8" />
            </motion.div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={staggerItem} className="space-y-6">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  A SojaTerra é uma startup agrícola angolana em fase de estruturação, pertencente à empresa Kussopa Comércio Geral, com foco na produção sustentável de soja no Planalto Central de Angola.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  O projeto está sendo implementado de forma faseada, com gestão técnica especializada e visão de consolidação regional. Nossa abordagem prioriza o desenvolvimento estruturado, a aprendizagem contínua e o compromisso com o crescimento sustentável do setor agrícola angolano.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Posicionados estrategicamente na Província do Bié, buscamos contribuir para a diversificação económica de Angola, fortalecimento da segurança alimentar nacional e desenvolvimento do interior do país.
                </p>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-2 gap-4">
                <img
                  src={IMAGES.SOYBEAN_FIELD_1}
                  alt="Campo de soja"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <img
                  src={IMAGES.AGRICULTURAL_MACHINERY_1}
                  alt="Operações mecanizadas"
                  className="w-full h-64 object-cover rounded-lg shadow-lg mt-8"
                />
                <img
                  src={IMAGES.AGRICULTURAL_TEAM_1}
                  alt="Equipa técnica"
                  className="w-full h-64 object-cover rounded-lg shadow-lg -mt-8"
                />
                <img
                  src={IMAGES.AFRICAN_AGRICULTURE_1}
                  alt="Agricultura no Planalto Central"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.MODELO_OPERACIONAL} className="py-32 bg-gradient-to-b from-background via-muted/20 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-7xl mx-auto"
          >
            {/* Header com destaque especial */}
            <motion.div variants={staggerItem} className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 rounded-full bg-secondary/10 border border-secondary/20">
                <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                <span className="text-sm font-medium text-secondary uppercase tracking-wider">Estrutura Operacional</span>
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent">
                Modelo Operacional
              </h2>
              <div className="w-32 h-1.5 bg-gradient-to-r from-secondary via-primary to-secondary mx-auto mb-10 rounded-full" />
              <p className="text-2xl md:text-3xl text-muted-foreground max-w-4xl mx-auto leading-relaxed font-light">
                Estrutura organizacional <span className="text-secondary font-semibold">disciplinada</span> com foco em crescimento <span className="text-primary font-semibold">progressivo</span> e consolidação regional
              </p>
            </motion.div>

            {/* Grid com imagens em destaque */}
            <motion.div
              variants={staggerContainer}
              className="grid lg:grid-cols-2 gap-12"
            >
              {operationalModel.map((feature, index) => (
                <motion.div 
                  key={index} 
                  variants={staggerItem}
                  className="group relative"
                >
                  {/* Card com imagem em destaque */}
                  <div className="relative overflow-hidden rounded-3xl bg-card border border-border/50 shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl hover:shadow-secondary/20">
                    {/* Imagem hero */}
                    <div className="relative h-80 overflow-hidden">
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      {/* Overlay gradiente */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      {/* Badge flutuante */}
                      <div className="absolute top-6 left-6 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-white/20">
                        <span className="text-sm font-semibold text-gray-800">0{index + 1}</span>
                      </div>
                    </div>
                    
                    {/* Conteúdo com tipografia destacada */}
                    <div className="p-8">
                      <h3 className="text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">
                        {feature.title}
                      </h3>
                      <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-light">
                        {feature.description}
                      </p>
                      
                      {/* Linha decorativa */}
                      <div className="mt-6 w-16 h-1 bg-gradient-to-r from-secondary to-primary rounded-full" />
                    </div>
                    
                    {/* Efeito de borda superior */}
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-secondary/40 to-transparent" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
            
            {/* Seção adicional com estatísticas visuais */}
            <motion.div 
              variants={staggerItem}
              className="mt-20 text-center"
            >
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                  <div className="text-4xl font-bold text-primary mb-2">100%</div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Mecanização</div>
                </div>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20">
                  <div className="text-4xl font-bold text-secondary mb-2">360°</div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Gestão Integrada</div>
                </div>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20">
                  <div className="text-4xl font-bold text-accent mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Monitoramento</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.SUSTENTABILIDADE} className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Sustentabilidade e Alinhamento Estratégico
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto mb-8" />
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Compromisso estratégico com os Objetivos de Desenvolvimento Sustentável e o Plano Nacional de Desenvolvimento de Angola
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="mb-16">
              <h3 className="text-2xl font-semibold mb-8 text-center">Objetivos de Desenvolvimento Sustentável</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {sdgGoals.map((goal) => (
                  <SDGCard key={goal.number} goal={goal} />
                ))}
              </div>
            </motion.div>

            <motion.div variants={staggerItem}>
              <h3 className="text-2xl font-semibold mb-8 text-center">
                Alinhamento com o Plano Nacional de Desenvolvimento
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {sustainabilityCommitments.map((commitment, index) => (
                  <CommitmentCard
                    key={index}
                    title={commitment.title}
                    description={commitment.description}
                    icon={
                      index === 0 ? <Globe className="w-8 h-8 text-primary" /> :
                      index === 1 ? <TrendingUp className="w-8 h-8 text-primary" /> :
                      index === 2 ? <Leaf className="w-8 h-8 text-primary" /> :
                      <Users className="w-8 h-8 text-primary" />
                    }
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.INTEGRACAO_TERRITORIAL} className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Integração Territorial</h2>
              <div className="w-24 h-1 bg-primary mx-auto mb-8" />
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Posicionamento estratégico no contexto do Corredor do Lobito
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={staggerItem} className="space-y-6">
                <h3 className="text-2xl font-semibold">Corredor do Lobito</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  O Corredor do Lobito representa um eixo estruturante para a industrialização nacional e regional, conectando o Porto do Lobito às regiões produtivas do interior de Angola e países vizinhos.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  A SojaTerra, localizada na Província do Bié, está estrategicamente posicionada para contribuir com o desenvolvimento desta importante infraestrutura logística, fortalecendo a cadeia de valor agrícola regional.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Este posicionamento reflete o compromisso com a integração territorial e o desenvolvimento sustentável do interior de Angola.
                </p>
              </motion.div>

              <motion.div variants={staggerItem}>
                <img
                  src={IMAGES.LOBITO_CORRIDOR_MAP_20260304_091127_62}
                  alt="Mapa do Corredor do Lobito"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.COMPROMISSO} className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Compromisso com o Desenvolvimento
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto mb-8" />
            </motion.div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={staggerItem}>
                <img
                  src={IMAGES.ANGOLA_AGRICULTURAL_INFRASTRUCTURE_20260304_091128_60}
                  alt="Infraestrutura agrícola"
                  className="w-full h-96 object-cover rounded-lg shadow-lg"
                />
              </motion.div>

              <motion.div variants={staggerItem} className="space-y-8">
                <div className="flex gap-4">
                  <Heart className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Geração de Valor Local</h3>
                    <p className="text-muted-foreground">
                      Criação de oportunidades económicas e fortalecimento da cadeia produtiva no Planalto Central.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <TrendingUp className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Fortalecimento da Cadeia Agrícola</h3>
                    <p className="text-muted-foreground">
                      Desenvolvimento de infraestrutura, transferência de conhecimento e consolidação do setor.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Leaf className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Crescimento Sustentável</h3>
                    <p className="text-muted-foreground">
                      Práticas agrícolas responsáveis com foco na preservação ambiental e gestão eficiente de recursos.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Globe className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Desenvolvimento do Interior</h3>
                    <p className="text-muted-foreground">
                      Contribuição estratégica para a diversificação económica e desenvolvimento regional de Angola.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id={SECTION_IDS.CONTACTO} className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Contacto Institucional</h2>
              <div className="w-24 h-1 bg-primary mx-auto mb-8" />
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="bg-card border border-border rounded-lg p-8 md:p-12 shadow-lg"
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold mb-2">{companyInfo.company}</h3>
                  <p className="text-muted-foreground">Kussopa Comércio Geral</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 pt-6">
                  <div className="flex gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium mb-1">Localização</p>
                      <p className="text-muted-foreground">{companyInfo.location}</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium mb-1">Email</p>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium mb-1">Telefone</p>
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  {companyInfo.website && (
                    <div className="flex gap-3">
                      <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Website</p>
                        <a
                          href={`https://${companyInfo.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {companyInfo.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {companyInfo.socialMedia && (
                  <div className="pt-6 border-t border-border">
                    <p className="font-medium mb-4">Redes Sociais</p>
                    <div className="flex gap-4">
                      {companyInfo.socialMedia.linkedin && (
                        <a
                          href={companyInfo.socialMedia.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          LinkedIn
                        </a>
                      )}
                      {companyInfo.socialMedia.facebook && (
                        <a
                          href={companyInfo.socialMedia.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          Facebook
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
